import {
    DefaultCommunitySearch,
    DefaultCommunitySearchLocationFilter,
    LocalizedDistance,
    Meters,
} from "@sp-crm/core";
import { useLocalizedStrings } from "components/locale-provider/locale-provider";
import { Input } from "components/ui/input";
import { Radio } from "components/ui/radio";
import { SelectPro } from "components/ui/select-pro";
import React, { useCallback, useMemo } from "react";
import { getStatesArray } from "util/states";

const allowedDistances = [1, 3, 5, 10, 15, 20, 25, 30, 50, 75, 100, 125, 150];

interface DefaultCommunitySearchEditorProps {
    value: DefaultCommunitySearch;
    onChange: (value: DefaultCommunitySearch) => void;
    disabled?: boolean;
}

export const DefaultCommunitySearchEditor: React.FC<
    DefaultCommunitySearchEditorProps
> = ({ value, onChange, disabled }) => {
    const locale = useLocalizedStrings();

    const handleSearchTypeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const type = e.target.value;
            if (type === "allCommunities") {
                onChange({ type: "allCommunities", state: value.state });
            } else {
                onChange({
                    type: "locationFilter",
                    address: "",
                    city: "",
                    state: value.state,
                    zip: "",
                    distance: locale.conversions.toMeters(50 as LocalizedDistance),
                });
            }
        },
        [onChange, value, locale.conversions],
    );

    const handleStateChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            onChange({ type: "allCommunities", state: e.target.value });
        },
        [onChange],
    );

    const prox =
        value.type === "locationFilter"
            ? (value as DefaultCommunitySearchLocationFilter)
            : null;

    const updateProximity = useCallback(
        (fields: Partial<DefaultCommunitySearchLocationFilter>) => {
            if (!prox) return;
            onChange({
                type: "locationFilter",
                address: prox.address,
                city: prox.city,
                state: prox.state,
                zip: prox.zip,
                distance: prox.distance,
                ...fields,
            });
        },
        [onChange, prox],
    );

    const handleDistanceChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            updateProximity({
                distance: locale.conversions.toMeters(
                    parseInt(e.target.value, 10) as LocalizedDistance,
                ),
            });
        },
        [updateProximity, locale.conversions],
    );

    const handleAddressChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            updateProximity({ address: e.target.value });
        },
        [updateProximity],
    );

    const handleCityChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            updateProximity({ city: e.target.value });
        },
        [updateProximity],
    );

    const handleProximityStateChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            updateProximity({ state: e.target.value });
        },
        [updateProximity],
    );

    const handleZipChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            updateProximity({ zip: e.target.value });
        },
        [updateProximity],
    );

    const distanceOptions = useMemo(
        () =>
            allowedDistances.map(d => ({
                value: String(d),
                text: `Within ${d} ${d > 1 ? locale.strings.distanceUnitPlural : locale.strings.distanceUnit}`,
            })),
        [locale.strings.distanceUnit, locale.strings.distanceUnitPlural],
    );

    const currentDistance = prox?.distance
        ? String(Math.floor(locale.conversions.fromMeters(prox.distance as Meters)))
        : "50";

    const statesArray = useMemo(() => getStatesArray(), []);

    return (
        <div className="space-y-1">
            <div>Default community search</div>
            <div className="text-sm text-gray-500">
                Choose which communities users see when they start a new community search.
            </div>
            <div className="space-y-0.5">
                <Radio
                    label="All communities"
                    value="allCommunities"
                    checked={value.type === "allCommunities"}
                    onChange={handleSearchTypeChange}
                    disabled={disabled}
                />
                <div className="ml-6 text-sm text-gray-500">
                    The selected state will be pre-filled when adding a location filter
                </div>
                {value.type === "allCommunities" ? (
                    <div className="ml-6 mt-1">
                        <SelectPro
                            includePlaceholderOption
                            disabled={disabled}
                            value={value.state}
                            onChange={handleStateChange}
                            options={statesArray}
                        />
                    </div>
                ) : null}
            </div>
            <div className="space-y-0.5">
                <Radio
                    label="Location filter"
                    value="locationFilter"
                    checked={value.type === "locationFilter"}
                    onChange={handleSearchTypeChange}
                    disabled={disabled}
                />
                <div className="ml-6 text-sm text-gray-500">
                    Applied on new searches and when filters are cleared
                </div>
                {value.type === "locationFilter" ? (
                    <div className="ml-6 mt-1 space-y-2">
                        <SelectPro
                            disabled={disabled}
                            value={currentDistance}
                            onChange={handleDistanceChange}
                            options={distanceOptions}
                        />
                        <Input
                            disabled={disabled}
                            value={prox?.address ?? ""}
                            onChange={handleAddressChange}
                            placeholder="Street Address (optional)"
                        />
                        <div className="flex space-x-2">
                            <div className="flex-1">
                                <Input
                                    disabled={disabled}
                                    value={prox?.city ?? ""}
                                    onChange={handleCityChange}
                                    placeholder="City"
                                />
                            </div>
                            <div className="flex-1">
                                <SelectPro
                                    disabled={disabled}
                                    value={prox?.state ?? ""}
                                    includePlaceholderOption
                                    placeholderStr="(select)"
                                    onChange={handleProximityStateChange}
                                    options={statesArray}
                                />
                            </div>
                            <div className="flex-1">
                                <Input
                                    disabled={disabled}
                                    value={prox?.zip ?? ""}
                                    onChange={handleZipChange}
                                    placeholder={locale.strings.zip}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};
